/**
 * `order.ts` — status & jenis peristiwa siklus pesanan (§6, §10).
 *
 * Lintas-lapis: ditulis `outlet-server`, dibaca `pos-web` (menentukan layar & tombol yang boleh
 * muncul) dan portal (laporan). Nilai yang hanya hidup di DDL akan diketik ulang di klien.
 *
 * @module
 */
/**
 * Status pesanan (§6).
 *
 * ⚠️ `sent` bukan sekadar label: ia **batas rezim kontrol**. Void SEBELUM kirim = koreksi ketik
 * (normal); void SESUDAH dapur memasak = wajib ditinjau (§10 `priority_review`). Bahan sudah
 * terpakai, biayanya sudah nyata.
 */
export const ORDER_STATUSES = Object.freeze([
    'draft', 'sent', 'in_progress', 'ready', 'served', 'billed', 'paid', 'void',
]);
const ORDER_STATUS_SET = new Set(ORDER_STATUSES);
/** Guard {@link OrderStatus}. */
export function isOrderStatus(v) {
    return typeof v === 'string' && ORDER_STATUS_SET.has(v);
}
/** Status baris pesanan (§6). `void` per baris berbeda dari void seluruh pesanan. */
export const ORDER_LINE_STATUSES = Object.freeze([
    'draft', 'sent', 'in_progress', 'ready', 'served', 'void',
]);
const LINE_STATUS_SET = new Set(ORDER_LINE_STATUSES);
/** Guard {@link OrderLineStatus}. */
export function isOrderLineStatus(v) {
    return typeof v === 'string' && LINE_STATUS_SET.has(v);
}
/**
 * Jenis peristiwa pada jejak `order_event` (§10, append-only P5).
 *
 * ⚠️ **Berbeda dari `EVENT_NAMES` (`events.ts`).** Yang ini jejak audit **internal** — halus,
 * lengkap, disimpan penuh. `EVENT_NAMES` adalah outbox **keluar** ke companion — kasar, tingkat
 * bill. Menyatukannya mengulang `Log_TranKeyFront`: 86 jt baris, 26× per transaksi (P7).
 */
export const ORDER_EVENT_TYPES = Object.freeze([
    'order_opened', 'line_added', 'line_qty_changed', 'line_voided', 'line_sent',
    'order_discount_applied', 'order_sent', 'tender_added', 'tender_reversed',
    'order_billed',
    /**
     * ⭐⭐ **Tagihan DIBATALKAN, pesanannya tidak** (Imron, 2026-08-23: *"ketika tekan tombol pay
     * yang pertama, tidak ada tombol cancel-nya"*).
     *
     * ⛔ Sengaja BUKAN `order_voided`. Void membunuh pesanannya — barisnya hilang, dan tamu yang cuma
     * ingin menambah satu item harus mengetik ulang seluruh bill di depan antrean. Yang dibatalkan di
     * sini cuma **hitungannya**: status mundur ke `sent`, baris pajak (data turunan) dihapus, dan
     * barisnya utuh untuk disunting lagi.
     *
     * ⛔⛔ Ia TAK PERNAH boleh terjadi kalau sudah ada tender: uang yang sudah tercatat tak bisa
     * "dibatalkan" diam-diam — itu wilayah `reopenOrder`, dengan gerbangnya sendiri.
     */
    'order_bill_cancelled',
    'order_paid', 'order_voided', 'order_refunded',
    /**
     * ⭐ **Tagihan (sebelum bayar) ≠ struk (sesudah bayar).** Dua dokumen berbeda dengan akibat
     * berbeda: tagihan diberikan tamu untuk diperiksa, struk adalah bukti pembayaran. Menyamakannya
     * membuat "berapa kali struk dicetak" tak bisa dijawab — dan itu pertanyaan pertama saat ada
     * dugaan struk ganda dipakai untuk klaim.
     */
    'bill_printed',
    'receipt_printed', 'receipt_reprinted',
    /**
     * ⭐ Pindah baris antar-bill — **fungsi transaksi berjalan yang paling sering dipakai**:
     * 2.626.726 baris = **8,1%** dari 32,4 juta baris VTEC. Dicatat di **KEDUA** pesanan, karena
     * jejak yang hanya ada di satu sisi membuat salah satu bill tampak berubah tanpa sebab.
     */
    'line_moved',
    /**
     * ⭐ Bill lunas dibuka kembali ("release payment"). ⛔ **Selalu `priority_review`** — angka yang
     * sudah masuk laporan berubah, dan itu tak boleh lewat tanpa dilihat manajer.
     */
    'order_reopened',
    /**
     * ⭐⭐ Selisih antara total yang DIKLAIM tablet dan total yang DIHITUNG server saat penjualan
     * luring diterima (K13 tier satu-tablet).
     *
     * ⛔ Bukan galat, dan sengaja bukan penolakan: tamunya sudah membayar dan sudah pulang. Yang
     * benar adalah menyimpan angka server sebagai kebenaran lalu **membunyikan** selisihnya, supaya
     * utak-atik jadi peristiwa yang terlihat alih-alih senyap (`OUTLET-DB-ENGINE.md` §5d).
     */
    'sale_variance',
    /**
     * ⭐⭐⭐ Harga yang **ditagihkan outlet** berbeda dari harga master saat dokumen luring diterima
     * (2026-08-25).
     *
     * ⛔ Sengaja BUKAN penolakan, dan ⛔ bukan pula galat: yang tersimpan justru harga OUTLET —
     * uangnya sudah berpindah tangan di depan tamu, dan portal yang memaksakan harga masternya cuma
     * membuat penjualan itu tak tercatat. Peristiwa ini yang membuat penyimpangannya **terlihat**,
     * dan sebabnya hampir selalu satu: perangkat itu belum SYNC (K25).
     *
     * ⚠️ Beda dari {@link 'sale_variance'}: yang ini "harga beda, diterima"; yang itu "mesin hitung
     * berselisih". Tindak lanjutnya berbeda — segarkan katalog vs periksa setelan pajak.
     */
    'sale_price_drift',
    /**
     * ⭐⭐ **Kunci sunting DIREBUT dari pemegang lain** ("paksa buka meja").
     *
     * ⛔ **Selalu `priority_review`.** Ia jalan yang sah — HP pelayan mati, dan mejanya harus bisa
     * dilanjutkan — tapi ia juga persis jalan yang dipakai untuk mengambil alih bill orang lain.
     * Satu-satunya yang membedakan keduanya adalah manajer yang melihatnya, jadi peristiwa ini
     * tak boleh menyatu ke dalam arus jejak biasa yang tak pernah dibaca siapa pun.
     *
     * ⚠️ Melepas kunci **tidak** dicatat: pelepasan terjadi pada tiap kirim-dapur dan tiap
     * penutupan, dan mencatatnya berarti menggandakan tabel jejak demi peristiwa yang tak pernah
     * ditanyakan siapa pun (pelajaran `log_sync` VTEC: 86 juta baris = 26× per transaksi).
     */
    'order_hold_taken',
    /**
     * ⭐ Pesanan **pindah meja** (VTEC: `FromTableID → ToTableID` di `log_order`).
     *
     * ⚠️ Ini peristiwa PESANAN, bukan peristiwa meja: yang berpindah adalah tagihannya, dan
     * pertanyaannya kelak selalu *"bill ini tadinya di meja mana?"* — bukan sebaliknya.
     */
    'table_changed',
    /**
     * ⭐ Koreksi **jumlah tamu**. Pelayan sering salah ketik saat membuka meja (Imron), dan angka
     * ini bukan hiasan: ia penyebut *average per cover* — laporan yang paling sering dipakai menilai
     * kinerja outlet. Salah ketik yang tak bisa dikoreksi akan **diperbaiki lewat void**, dan void
     * demi salah ketik adalah kontrol yang aus karena dipakai untuk hal yang bukan urusannya.
     */
    'guest_count_changed',
    /**
     * ⭐ Pesanan diberi PENANDA (meja/antrean/nama) + jumlah tamu, **tanpa ditahan** (0070).
     *
     * ⛔ Bukan `order_parked`: menahan berarti bill keluar dari layar kasir, menandai tidak.
     * Menyatukannya membuat *"kenapa bill saya hilang?"* tak bisa dijawab dari jejaknya sendiri.
     */
    'order_tagged',
]);
const EVENT_TYPE_SET = new Set(ORDER_EVENT_TYPES);
/** Guard {@link OrderEventType}. */
export function isOrderEventType(v) {
    return typeof v === 'string' && EVENT_TYPE_SET.has(v);
}
/** Status sesi kas (§4b ③ — `shift` ≠ `attendance`). */
export const SHIFT_STATUSES = Object.freeze(['open', 'closed']);
/** Guard {@link ShiftStatus}. */
export function isShiftStatus(v) {
    return v === 'open' || v === 'closed';
}
/**
 * ⭐ Transisi status pesanan yang SAH (§6).
 *
 * Ditegakkan, bukan didokumentasikan (P6). Yang paling penting: **tak ada busur keluar dari
 * `paid` maupun `void`** — koreksi atas pesanan yang sudah lunas adalah `order.refunded`,
 * yaitu **peristiwa baru**, bukan penyuntingan status (P5).
 */
export const ORDER_TRANSITIONS = Object.freeze({
    draft: Object.freeze(['sent', 'billed', 'void']),
    sent: Object.freeze(['in_progress', 'ready', 'served', 'billed', 'void']),
    in_progress: Object.freeze(['ready', 'served', 'billed', 'void']),
    ready: Object.freeze(['served', 'billed', 'void']),
    served: Object.freeze(['billed', 'void']),
    billed: Object.freeze(['paid', 'void']),
    paid: Object.freeze([]),
    void: Object.freeze([]),
});
/** Apakah transisi status pesanan sah? */
export function canTransitionOrder(from, to) {
    return ORDER_TRANSITIONS[from].includes(to);
}
/**
 * Apakah void pada status ini WAJIB ditinjau manajer? (§10 `priority_review`)
 *
 * Diambil dari `log_actiondesp` VTEC: void sebelum kirim = normal; sesudah dapur memasak = wajib
 * ditinjau. Ini kontrol yang MEMAKSA (P6), bukan pencatatan.
 */
export function voidRequiresReview(status) {
    return status !== 'draft';
}
//# sourceMappingURL=order.js.map